/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';

export type DeviceInputProps = {
  index: number;
  label: string;
  value: string;
  active: boolean;
};

export const useStyles = makeStyles((theme: DLSTheme) => ({
  popupContainer : {
    display       : 'flex',
    flexDirection : 'row',
    alignItems    : 'center',
    margin        : '0px 0px 0px 0px'
  },
  cardValue : {
    ...dlsAlt('typography', 'label3'),
    backgroundColor : ({active}: {active: boolean}) => active ? dlsColor('caBlue') : dlsColor('white'),
    color           : ({active}: {active: boolean}) => active ? dlsColor('white') : dlsColor('caBlue'),
    textAlign       : 'center',
    boxSizing       : 'border-box',
    borderRadius    : '2px',
    border          : `2px solid ${dlsColor('caBlue')}`,
    padding         : '0px 4px',
    minWidth        : '32px'
  }
}));

/**
 * @DetailCardInputs
 * This component is used in the AssetDetailsDiagnostics component to display the input status for each input.
 * 
 * @param index - The index of the input.
 * @param label - The label of the input.
 * @param value - The value of the input.
 * @param active - The active status of the input.
 * 
 * @returns A component that displays the input status for each input's label and value via a card item row.
 * 
 * @example
 * ```typescript
 * 
 * import DetailCardInputs from '@ion/dls/src/components/inputs/DetailCardInputs';
 * 
 * <SomeComponent>
 *  <DetailCardInputs index={index} label={label} value={value} active={active} />
 * </SomeComponent>
 * 
 * ```
 */
const DetailCardInputs = ({
  index,
  label,
  value,
  active
}: DeviceInputProps) => {
  const classes = useStyles({active});
  return (
    <CardItemRow key={index}
      label={label}
      value={
        <div className={classes.cardValue}>
          {value}
        </div>
      }
      isCondensedVariant
      isBottomMargin />
  );
};

export default DetailCardInputs;
